import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Compass, Layers } from 'lucide-react';
import SectionHeading from '@/components/site/SectionHeading';
import Reveal from '@/components/site/Reveal';
import Stats from '@/components/home/Stats';
import Founder from '@/components/home/Founder';
import WhyChoose from '@/components/home/WhyChoose';
import { Button } from '@/components/ui/button';

const PageHeader = ({ kicker, title, subtitle, image }) => (
  <section className="relative isolate overflow-hidden bg-[#144726] pt-32 pb-20 md:pt-40 md:pb-24">
    <img src={image} alt="" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-25" />
    <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#144726] via-[#144726]/85 to-[#144726]" />
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#C6A35B]">{kicker}</p>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight max-w-3xl">{title}</h1>
      <p className="mt-5 max-w-2xl text-base md:text-lg text-white/75">{subtitle}</p>
    </div>
  </section>
);

export default function AboutPage() {
  useEffect(() => { document.title = 'About Cespedis | Commercial Real Estate Hyderabad'; }, []);
  return (
    <div data-testid="about-page">
      <PageHeader
        kicker="About Cespedis"
        title="Building Value Beyond Leasing."
        subtitle="A specialized commercial real estate and property management firm, helping landlords, developers, and investors achieve superior returns from their assets."
        image="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=2400&q=80"
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <SectionHeading kicker="Our story" title="Discipline applied to commercial real estate." />
                <div className="mt-6 space-y-5 text-slate-700 leading-relaxed">
                  <p>Cespedis was founded with a simple conviction: that disciplined leasing and management can meaningfully shift the long-term performance of any commercial asset. The Hyderabad commercial market has matured into one of India's most attractive Tier-1 markets, yet many owners still operate without the institutional rigour that compounds returns over time.</p>
                  <p>We bring a documented, measurable approach to every mandate — from positioning and benchmarking, through tenant outreach, lease negotiation, and ongoing property operations. Our objective is to make ownership of commercial real estate simpler, more predictable, and meaningfully more profitable.</p>
                  <p>Our team brings together specialists from leasing, asset management, marketing, and operations. We work with landlords, developers, GCCs, retail brands, coworking operators, and investors across Hyderabad and select Indian metros.</p>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={150}>
                <div className="grid grid-cols-1 gap-5">
                  <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#C6A35B]/15 text-[#144726]"><Target className="h-5 w-5" /></div>
                    <h3 className="mt-4 font-semibold text-[#144726]">Mission</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">Deliver professional real estate solutions through integrity, market expertise, and long-term client partnerships.</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#C6A35B]/15 text-[#144726]"><Eye className="h-5 w-5" /></div>
                    <h3 className="mt-4 font-semibold text-[#144726]">Vision</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">Become India’s most trusted commercial leasing and property management partner.</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#C6A35B]/15 text-[#144726]"><Compass className="h-5 w-5" /></div>
                    <h3 className="mt-4 font-semibold text-[#144726]">Values</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">Integrity. Discipline. Transparency. Long-term partnership over short-term wins.</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#C6A35B]/15 text-[#144726]"><Layers className="h-5 w-5" /></div>
                    <h3 className="mt-4 font-semibold text-[#144726]">Approach</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">Benchmark-led pricing. Conflict-free representation. Measurable rhythms.</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <WhyChoose />
      <Stats />
      <Founder />

      <section className="bg-[#144726] text-white py-16 md:py-20">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 lg:px-8 md:flex-row md:items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C6A35B]">Ready to start?</p>
            <h3 className="mt-2 font-display text-3xl md:text-4xl font-semibold">Let's discuss your asset or requirement.</h3>
          </div>
          <Link to="/contact"><Button className="h-12 rounded-lg bg-[#C6A35B] px-6 text-[#144726] hover:bg-[#B8923E]">Schedule a Consultation <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
        </div>
      </section>
    </div>
  );
}
